// src/components/ProposalCard.js
import React from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';
import {
  CalendarIcon,
  UserCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';

const ProposalCard = ({ proposal }) => {
  // 日付が有効かチェック
  const isValidDate = dateString => {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  // 提案のステータスを取得
  const getProposalStatus = () => {
    if (!proposal || !isValidDate(proposal.startTime) || !isValidDate(proposal.endTime)) {
      return { status: 'unknown', text: '不明', color: 'bg-gray-100 text-gray-800' };
    }

    const now = new Date();
    const startTime = new Date(proposal.startTime);
    const endTime = new Date(proposal.endTime);

    if (now < startTime) {
      return {
        status: 'pending',
        text: '準備中',
        color: 'bg-yellow-100 text-yellow-800',
        timeText: `${formatDistanceToNow(startTime, { locale: ja })}後に開始`,
      };
    } else if (now > endTime) {
      return {
        status: 'closed',
        text: '終了',
        color: 'bg-gray-100 text-gray-800',
        timeText: `${formatDistanceToNow(endTime, { locale: ja })}前に終了`,
      };
    } else {
      return {
        status: 'active',
        text: '進行中',
        color: 'bg-green-100 text-green-800',
        timeText: `${formatDistanceToNow(endTime, { locale: ja })}後に終了`,
      };
    }
  };

  // 投票方式
  const getVotingMethod = () => {
    return proposal.votingMethod === 'ONE_ADDRESS_ONE_VOTE' ? '1アドレス1票' : 'コイン量比例';
  };

  // 投票数を計算
  const getVoteCount = () => {
    if (!proposal.votes) return 0;
    return proposal.votes.length;
  };

  // コメント数を計算
  const getCommentCount = () => {
    return proposal._count?.comments || 0;
  };

  // トップの選択肢を取得
  const getTopChoice = () => {
    if (!proposal.votes || proposal.votes.length === 0) return null;

    // 各選択肢の票数を集計
    const voteCounts = {};

    proposal.votes.forEach(vote => {
      const choiceId = vote.choiceId;
      if (!voteCounts[choiceId]) {
        voteCounts[choiceId] = {
          count: 0,
          weight: 0,
        };
      }

      voteCounts[choiceId].count += 1;
      voteCounts[choiceId].weight += parseInt(vote.weight || '1');
    });

    // 最大の票数を持つ選択肢を特定
    let topChoiceId = null;
    let maxWeight = 0;

    for (const [choiceId, data] of Object.entries(voteCounts)) {
      if (data.weight > maxWeight) {
        maxWeight = data.weight;
        topChoiceId = choiceId;
      }
    }

    if (!topChoiceId) return null;

    // 選択肢テキストを特定
    const topChoice = proposal.choices.find(c => c.id === topChoiceId);
    if (!topChoice) return null;

    return {
      text: topChoice.text,
      count: voteCounts[topChoiceId].count,
      weight: maxWeight,
    };
  };

  const proposalStatus = getProposalStatus();
  const topChoice = getTopChoice();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden transition transform hover:-translate-y-1 hover:shadow-md h-96">
      <Link to={`/proposals/${proposal.id}`} className="block h-full flex flex-col">
        {/* カードの内容をフレックスコンテナとして構成 */}
        <div className="p-5 flex-grow flex flex-col">
          {/* 状態バッジ */}
          <div className="flex justify-between items-center mb-3">
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${proposalStatus.color}`}
            >
              {proposalStatus.text}
            </span>

            <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
              <ClockIcon className="h-4 w-4 mr-1" />
              {proposalStatus.timeText}
            </span>
          </div>

          {/* タイトル */}
          <h3 className="text-lg font-bold mb-2 line-clamp-2 text-gray-900 dark:text-gray-100">
            {proposal.title}
          </h3>

          {/* 期間 - 提案者の上に表示するよう移動 */}
          <div className="flex items-center mb-2">
            <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {isValidDate(proposal.startTime)
                ? format(new Date(proposal.startTime), 'yyyy/MM/dd', { locale: ja })
                : '日時不明'}{' '}
              -{' '}
              {isValidDate(proposal.endTime)
                ? format(new Date(proposal.endTime), 'yyyy/MM/dd', { locale: ja })
                : '日時不明'}
            </span>
          </div>

          {/* 提案者 */}
          <div className="flex items-center mb-3">
            <UserCircleIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
            <Link
              to={`/user/${proposal.creatorAddress}`}
              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              onClick={e => e.stopPropagation()}
            >
              {proposal.creatorAddress.substring(0, 6)}...
            </Link>

            {/* 投票方式を提案者の右側に表示 */}
            <span className="mx-2 text-xs text-gray-500 dark:text-gray-400">•</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {getVotingMethod()}
              {proposal.includeUnclaimed && ' (未Claim含む)'}
            </span>
          </div>

          {/* コイン情報 - 名前と記号を表示 */}
          <div className="mb-4">
            <div className="inline-flex items-center bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-md text-xs">
              <svg
                className="h-3 w-3 mr-1"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 16V8M15 12H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>
                {proposal.token
                  ? `${proposal.token.name} (${proposal.token.symbol})`
                  : 'コイン不明'}
              </span>
            </div>
          </div>

          {/* 説明 - flex-growで可能な限り拡大し、overflow-autoでスクロール可能に */}
          <div className="flex-grow overflow-hidden">
            <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2">
              {proposal.description}
            </p>

            {/* 投票状況 */}
            {proposalStatus.status === 'closed' && topChoice && (
              <div className="mt-4 p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                <div className="text-xs text-gray-600 dark:text-gray-400">投票結果</div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  1位: {topChoice.text} ({topChoice.weight.toLocaleString()} 票)
                </div>
              </div>
            )}
          </div>

          {/* メタ情報 - 下部に固定 */}
          <div className="mt-4 pt-2 flex items-center justify-between border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {getVoteCount()}
                {getVotingMethod() === 'TOKEN_WEIGHTED' ? '人の投票' : '票'}
              </span>
            </div>

            <div className="flex items-center">
              <ChatBubbleLeftIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {getCommentCount()}件のコメント
              </span>
            </div>
          </div>
        </div>

        {/* フッター - 常に下部に固定 */}
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 flex justify-end items-center mt-auto">
          <span className="text-blue-600 dark:text-blue-400 font-medium">詳細を見る →</span>
        </div>
      </Link>
    </div>
  );
};

export default ProposalCard;
